import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Formik, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import useFetchAdjusters from '~/Adjuster/useFetchAdjusters';
import EditorFormik from '~/components/communications/EmailCommunicationCard/EmailEditor/EditorFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Caption from '~/components/core/TextComponents/Caption';
import Text from '~/components/core/TextComponents/Text';
import useDataFetcher from '~/components/useDataFetcher';
import { reportAxiosError } from '~/Utils';
import { validateNoneEmptyHtmlContent } from '~/Utils/validationUtils';

import ButtonsContainer from './core/Atomic/Buttons/ButtonsContainer';
import type {
  OutOfOfficeDialogProps,
  OutOfOfficeFormValues,
  OutOfOfficeInnerProps,
  TemplateTokensResponse,
} from './types/user-menu-types';
import CardDialog from './CardDialog';
import CheckboxFormik from './CheckboxFormik';
import useOrganization from './OrganizationContext';
import { DatePickerTextFieldFormik, MultiSelectIdLabelTextFieldFormik } from './TextFieldFormik';

import { useStyles } from '../assets/styles';

const OUT_OF_OFFICE_EMAIL_TOKENS = ['USER_OOO_RETURN_DATE', 'USER_FULL_NAME', 'USER_PHONE_NUMBER', 'USER_EMAIL'];

function OutOfOfficeDialog({
  user,
  onClose,
  currentSharedUsersIds,
  onSubmit,
  outOfOfficeData,
  isErrorOooData,
  isLoadingOooData,
}: OutOfOfficeDialogProps) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error TS233  useOrganization() not properly typed yet
  const { isTwilioEnabled } = useOrganization();

  const isForwardUserIdsExist = (outOfOfficeData?.forward_notification_user_ids || []).length > 0;

  return (
    <Formik
      initialValues={{
        should_forward_notification: isForwardUserIdsExist,
        forward_notification_user_ids: outOfOfficeData?.forward_notification_user_ids || [],
        should_use_out_of_office_voice_mail: isTwilioEnabled,
        back_to_office_date: outOfOfficeData?.back_to_office_date || '',
        ooo_email_content: outOfOfficeData?.ooo_email_content || '',
        should_send_out_of_office_email: outOfOfficeData?.should_send_out_of_office_email || false,
      }}
      validationSchema={Yup.object().shape({
        should_forward_notification: Yup.boolean().required('Required'),
        forward_notification_user_ids: Yup.array().when('should_forward_notification', {
          is: true,
          then: Yup.array().min(1, 'Required'),
        }),
        should_use_out_of_office_voice_mail: Yup.boolean().required('Required'),
        back_to_office_date: Yup.date()
          .required('Required')
          .min(new Date(), 'Return date must be set to a future date'),
        ooo_email_content: Yup.string().when('should_send_out_of_office_email', {
          is: true,
          then: Yup.string()
            .trim()
            .required('Email content is required')
            .test('not-empty-html', 'Email content is required', validateNoneEmptyHtmlContent),
          otherwise: Yup.string().notRequired(),
        }),
      })}
      enableReinitialize
      onSubmit={async (values) => {
        await onSubmit(values);
        onClose();
      }}
    >
      {() => {
        return (
          <CardDialog title="Out Of Office" isDialog onClose={onClose} maxWidth="md" fullWidth>
            <OutOfOfficeInner
              onClose={onClose}
              currentSharedUsersIds={currentSharedUsersIds}
              user={user}
              isErrorOooData={isErrorOooData}
              isLoadingOooData={isLoadingOooData}
            />
          </CardDialog>
        );
      }}
    </Formik>
  );
}

const OutOfOfficeInner: React.FC<OutOfOfficeInnerProps> = ({
  user,
  onClose,
  currentSharedUsersIds,
  isLoadingOooData,
  isErrorOooData,
}) => {
  const { classes } = useStyles();
  const { values, isSubmitting, handleSubmit } = useFormikContext<OutOfOfficeFormValues>();
  const { isLoadingPossibleAdjusters, isErrorPossibleAdjusters, possibleAdjustersWithDaSpecialist } = useFetchAdjusters(
    user.organization_id
  );
  const [possibleAdjustersIdLabel, setPossibleAdjustersIdLabel] = useState<Array<{ id: string; label: string }>>([]);
  const [isLoadingSharedUsers, setIsLoadingSharedUsers] = useState(false);
  const [editorKey, setEditorKey] = useState(uuidv4());

  const {
    data: tokensData,
    isLoading: isLoadingTokens,
    isError: isErrorTokens,
  } = useDataFetcher<TemplateTokensResponse>(
    `/api/v1/generic_templates/organization/${user.organization_id}/template/tokens?`,
    {
      params: {
        tokens_subset: OUT_OF_OFFICE_EMAIL_TOKENS,
      },
    }
  );

  // Due to Editors' tight coupling with their initial content, direct updates don't trigger re-renders. By updating the component's key via useEffect,
  // we force a rerender to reflect content changes, ensuring the UI stays updated
  useEffect(() => {
    setEditorKey(uuidv4());
  }, [values.should_send_out_of_office_email]);

  const mapTokens = (tokensData: TemplateTokensResponse | undefined) => {
    return (
      tokensData?.tokens?.map((token) => ({
        label: token.display_name,
        id: token.token_key,
        url_safe: token.url_safe,
      })) || []
    );
  };

  const emailTokens = mapTokens(tokensData);

  const getSharedUserIds = useCallback(async () => {
    try {
      setIsLoadingSharedUsers(true);
      const response = await axios.get(`/api/v1/users/${user.id}/share_notifications`);
      setIsLoadingSharedUsers(false);

      return response.data;
    } catch (error) {
      await reportAxiosError(error);
    }
  }, [user]);

  useEffect(() => {
    const setPossibleAdjusters = async () => {
      if (isLoadingPossibleAdjusters) {
        return;
      }
      let sharedUserIds = currentSharedUsersIds;
      if (!sharedUserIds) {
        sharedUserIds = await getSharedUserIds();
      }

      let possibleAdjusters = possibleAdjustersWithDaSpecialist.filter((adjuster) => adjuster.id !== user.id);
      possibleAdjusters = possibleAdjusters.filter((adjuster) => !sharedUserIds?.includes(adjuster.id));
      const options = possibleAdjusters.map((adjuster) => ({ id: adjuster.id, label: adjuster.username || '' }));
      setPossibleAdjustersIdLabel(options);
    };
    setPossibleAdjusters();
  }, [getSharedUserIds, currentSharedUsersIds, possibleAdjustersWithDaSpecialist, isLoadingPossibleAdjusters, user.id]);

  const SkeletonLoader = () => {
    return <SkeletonTable rowsCount={7} columnsCount={1} />;
  };

  return (
    <LoadingSwitch
      isLoading={!!isLoadingPossibleAdjusters || !!isLoadingSharedUsers || !!isLoadingTokens || !!isLoadingOooData}
      isError={!!isErrorPossibleAdjusters || !!isErrorTokens || !!isErrorOooData}
      LoadingComponent={SkeletonLoader}
    >
      <div className="container m-auto grid grid-cols-2 gap-8">
        <div>
          <CheckboxFormik id="should_forward_notification" label="Share notifications" />
          {values['should_forward_notification'] && (
            <div className="mb-4">
              <Caption variant={Caption.VARIANTS.LABEL}>
                Sharing with the selected users will end when you return to the office
              </Caption>
            </div>
          )}
        </div>
        <div>
          {values['should_forward_notification'] && (
            <MultiSelectIdLabelTextFieldFormik
              id="forward_notification_user_ids"
              select
              label="User"
              className={classes.textField}
              fullWidth
              options={possibleAdjustersIdLabel}
              renderValue={(selectedAdjusters: Array<{ id: string; label: string }>) =>
                selectedAdjusters.map((adjuster) => adjuster.label).join(', ')
              }
            />
          )}
        </div>
      </div>
      <div className="container m-auto mt-8 grid grid-cols-2 gap-8">
        {user.is_call_center_allowed && (
          <div>
            <CheckboxFormik id="should_use_out_of_office_voice_mail" label="Update my voicemail message" disabled />
          </div>
        )}
        <div className="mb-12">
          <DatePickerTextFieldFormik
            id="back_to_office_date"
            label="Back to office date"
            className={classes.textField}
            disabled={isSubmitting}
            disablePast
            fullWidth
          />
        </div>
      </div>
      <div>
        <div>
          <CheckboxFormik id="should_send_out_of_office_email" label="Send out-of-office email" />
        </div>
        <div>
          <Text variant="sm" className="mb-2 mt-12">
            Email Content
          </Text>
          <EditorFormik
            key={editorKey}
            id="ooo_email_content"
            classNameEditor="min-h-[150px] overflow-auto"
            displayConfig={{
              font: true,
              textColor: true,
              textStyle: true,
              align: true,
              link: true,
              suggestions: true,
              images: true,
            }}
            suggestionOptions={emailTokens}
            disabled={!values.should_send_out_of_office_email}
            hideContent={!values.should_send_out_of_office_email}
          />
        </div>
      </div>
      <div>
        <ButtonsContainer>
          <CancelButton onClick={onClose} />
          <Button variant="contained" color="primary" onClick={() => handleSubmit()} disabled={isSubmitting}>
            Set
          </Button>
        </ButtonsContainer>
      </div>
    </LoadingSwitch>
  );
};

export default OutOfOfficeDialog;
